import React, { useState, useEffect, useRef } from 'react';
import { mysteryBoxAPI } from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  Gift, Plus, Trash2, Coins, RefreshCw, AlertCircle, Check, Clock, 
  Search, Calendar, ChevronDown, ChevronUp, FileText, Bold, Italic, 
  List, ListOrdered, Heading2, Save, Layers
} from 'lucide-react';
import JackpotSequencesManager from './JackpotSequencesManager';
import MysteryBoxPrizesConfig from './MysteryBoxPrizesConfig';

const MysteryBoxManagement = () => {
  const [config, setConfig] = useState(null);
  const [localConfig, setLocalConfig] = useState(null); // Estado local para edição
  const [configChanged, setConfigChanged] = useState(false); // Flag de alterações pendentes
  const [savingConfig, setSavingConfig] = useState(false); // Flag de salvamento
  const [prizes, setPrizes] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('config');
  const [historyFilter, setHistoryFilter] = useState('');
  const [expandedDays, setExpandedDays] = useState({});
  const [rulesContent, setRulesContent] = useState('');
  const [savingRules, setSavingRules] = useState(false);
  const editorRef = useRef(null);
  
  // Jackpot States
  const [jackpot, setJackpot] = useState(null);
  const [localJackpot, setLocalJackpot] = useState(null);
  const [jackpotChanged, setJackpotChanged] = useState(false);
  const [savingJackpot, setSavingJackpot] = useState(false);
  
  // Weekly Winners States
  const [weeklyWinners, setWeeklyWinners] = useState([]);
  const [loadingWeeklyWinners, setLoadingWeeklyWinners] = useState(false);
  const [generatingPrizes, setGeneratingPrizes] = useState(false);
  const [dedupingPrizes, setDedupingPrizes] = useState(false);
  const [, setExpiredBoxes] = useState([]);
  const [, setExpiredStats] = useState(null);
  const [, setExpiringBoxes] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configRes, statsRes, jackpotRes] = await Promise.all([
        mysteryBoxAPI.getConfig(),
        mysteryBoxAPI.getStats(),
        mysteryBoxAPI.getJackpotConfig()
      ]);
      
      setConfig(configRes.data);
      setLocalConfig(configRes.data); // Inicializa estado local com dados do servidor
      setConfigChanged(false);
      setStats(statsRes.data.stats);
      setPrizes(statsRes.data.prizes || []);
      setRulesContent(configRes.data?.rules || '');
      
      // Jackpot
      setJackpot(jackpotRes.data);
      setLocalJackpot(jackpotRes.data);
      setJackpotChanged(false);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (kakoId = null) => {
    try {
      const params = { days: 30 };
      if (kakoId) params.kako_id = kakoId;
      
      const res = await mysteryBoxAPI.getAdminHistory(params);
      setHistory(res.data);
      
      // Expandir automaticamente o dia mais recente
      if (res.data?.history?.length > 0) {
        const latestDate = res.data.history[0].date;
        setExpandedDays(prev => ({ ...prev, [latestDate]: true }));
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico');
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
    if (activeTab === 'winners') {
      loadWeeklyWinners();
    }
  }, [activeTab]);

  const loadWeeklyWinners = async () => {
    setLoadingWeeklyWinners(true);
    try {
      const response = await mysteryBoxAPI.getWeeklyWinners();
      setWeeklyWinners(response.data.weeks || []);
    } catch (error) {
      console.error('Erro ao carregar ganhadores semanais:', error);
      toast.error('Erro ao carregar ganhadores');
    } finally {
      setLoadingWeeklyWinners(false);
    }
  };

  const handleGeneratePrizes = async () => {
    if (generatingPrizes) return;
    
    setGeneratingPrizes(true);
    try {
      const response = await mysteryBoxAPI.generateWeeklyPrizes();
      if (response.data.success) {
        toast.success(`Prêmios gerados: ${response.data.prizes_created?.length || 0} ganhadores`);
        loadWeeklyWinners(); // Recarregar lista
      } else {
        toast.warning(response.data.message || 'Nenhum ganhador encontrado');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao gerar prêmios';
      toast.error(errorMsg);
    } finally {
      setGeneratingPrizes(false);
    }
  };

  const dedupeWeeklyPrizes = async () => {
    if (!window.confirm('⚠️ Limpar duplicatas de ganhadores semanais?\n\nMantém apenas 1 entrada por (semana + posição). Critério: prioriza quem JÁ resgatou > mais antigo.\n\nCria índices únicos para impedir novas duplicatas.\n\nContinuar?')) return;
    setDedupingPrizes(true);
    try {
      const res = await mysteryBoxAPI.dedupeWeeklyPrizes();
      toast.success(`${res.data.deleted_duplicates} duplicata(s) removida(s) de ${res.data.duplicate_groups_found} grupo(s)`);
      loadWeeklyWinners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao limpar duplicatas');
    } finally {
      setDedupingPrizes(false);
    }
  };

  const loadExpiredBoxes = async () => {
    try {
      const response = await mysteryBoxAPI.getExpiredBoxes(30);
      setExpiredBoxes(response.data.items || []);
      setExpiredStats({
        total: response.data.total,
        total_prizes_lost: response.data.total_prizes_lost,
        total_prizes_returned: response.data.total_prizes_returned
      });
    } catch (error) {
      console.error('Erro ao carregar baús expirados:', error);
    }
  };

  const handleExpireBoxes = async () => {
    if (!window.confirm('Expirar todos os baús pendentes de dias anteriores?\n\nOs prêmios não resgatados serão retornados para o estoque.')) {
      return;
    }
    
    setExpiringBoxes(true);
    try {
      const response = await mysteryBoxAPI.expireBoxes();
      toast.success(response.data.message);
      loadExpiredBoxes();
      loadData(); // Recarregar stats
    } catch (error) {
      toast.error('Erro ao expirar baús');
    } finally {
      setExpiringBoxes(false);
    }
  };

  const updateConfig = async (field, value) => {
    try {
      await mysteryBoxAPI.updateConfig({ [field]: value });
      setConfig(prev => ({ ...prev, [field]: value }));
      toast.success('Configuração atualizada!');
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  // Atualiza o estado local sem salvar no servidor
  const updateLocalConfig = (field, value) => {
    setLocalConfig(prev => ({ ...prev, [field]: value }));
    setConfigChanged(true);
  };

  // Salva todas as configurações de uma vez
  const saveAllConfig = async () => {
    if (!configChanged || !localConfig) return;
    
    setSavingConfig(true);
    try {
      const configToSave = {
        goal_1: localConfig.goal_1,
        goal_2: localConfig.goal_2,
        goal_3: localConfig.goal_3,
        win_chance: localConfig.win_chance,
        weekly_prize_1st: localConfig.weekly_prize_1st,
        weekly_prize_2nd: localConfig.weekly_prize_2nd,
        weekly_prize_3rd: localConfig.weekly_prize_3rd,
      };
      
      await mysteryBoxAPI.updateConfig(configToSave);
      setConfig(localConfig);
      setConfigChanged(false);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingConfig(false);
    }
  };

  // Descarta alterações não salvas
  const discardChanges = () => {
    setLocalConfig(config);
    setConfigChanged(false);
    toast.info('Alterações descartadas');
  };

  // ===== JACKPOT FUNCTIONS =====
  
  const updateLocalJackpot = (field, value) => {
    setLocalJackpot(prev => ({ ...prev, [field]: value }));
    setJackpotChanged(true);
  };

  const saveJackpotConfig = async () => {
    if (!jackpotChanged || !localJackpot) return;
    
    setSavingJackpot(true);
    try {
      const configToSave = {
        active: localJackpot.active,
        target_coins: localJackpot.target_coins,
        prize_coins: localJackpot.prize_coins,
        num_winners: localJackpot.num_winners || 1,
        total_chests: localJackpot.total_chests,
        cooldown_seconds: localJackpot.cooldown_seconds,
      };
      
      await mysteryBoxAPI.updateJackpotConfig(configToSave);
      setJackpot(localJackpot);
      setJackpotChanged(false);
      toast.success('Configurações do Jackpot salvas!');
    } catch (error) {
      toast.error('Erro ao salvar configurações do Jackpot');
    } finally {
      setSavingJackpot(false);
    }
  };

  const discardJackpotChanges = () => {
    setLocalJackpot(jackpot);
    setJackpotChanged(false);
    toast.info('Alterações descartadas');
  };

  const resetJackpot = async () => {
    if (!window.confirm('⚠️ RESETAR JACKPOT?\n\nIsso vai:\n- Zerar o progresso\n- Remover TODOS os ganhadores\n- Reiniciar contagem de baús\n\nEssa ação não pode ser desfeita!')) {
      return;
    }
    
    try {
      await mysteryBoxAPI.resetJackpot();
      toast.success('Jackpot resetado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao resetar jackpot');
    }
  };

  const clearMysteryBoxHistory = async () => {
    if (!window.confirm('⚠️ APAGAR TODO O HISTÓRICO DO BAÚ MISTERIOSO?\n\nIsso vai apagar PERMANENTEMENTE:\n- Todas as aberturas de baú (moedas antigas + pontos atuais)\n- Todos os resgates do Mega Sortudos\n\nA configuração de prêmios será PRESERVADA.\n\nAção IRREVERSÍVEL. Continuar?')) {
      return;
    }
    if (!window.confirm('🚨 ÚLTIMA CONFIRMAÇÃO\n\nDigite OK para apagar TODO o histórico do Baú Misterioso em produção.')) {
      return;
    }
    try {
      const res = await mysteryBoxAPI.clearHistory();
      toast.success(`Histórico apagado! ${res.data.results_deleted} aberturas + ${res.data.weekly_prizes_deleted} resgates removidos.`);
      loadData();
    } catch (error) {
      toast.error('Erro ao apagar histórico do Baú Misterioso');
    }
  };

  const clearJackpotHistory = async () => {
    if (!window.confirm('⚠️ APAGAR TODO O HISTÓRICO DO JACKPOT?\n\nIsso vai apagar PERMANENTEMENTE:\n- Todos os ganhadores\n- Todos os cliques registrados\n- Toda a sorte acumulada por usuário\n\nA configuração do Jackpot e sequências serão PRESERVADAS.\n\nAção IRREVERSÍVEL. Continuar?')) {
      return;
    }
    if (!window.confirm('🚨 ÚLTIMA CONFIRMAÇÃO\n\nDigite OK para apagar TODO o histórico do Jackpot em produção.')) {
      return;
    }
    try {
      const res = await mysteryBoxAPI.clearJackpotHistory();
      toast.success(`Histórico apagado! ${res.data.winners_deleted} ganhadores + ${res.data.clicks_deleted} cliques + ${res.data.luck_deleted} sorte acumulada removidos.`);
      loadData();
    } catch (error) {
      toast.error('Erro ao apagar histórico do Jackpot');
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  // ===== Funções legadas (mantidas como no-op para compat) =====
  // Sistema migrado para pontos — admin gerencia prêmios via componente MysteryBoxPrizesConfig
  const addPrize = async () => {
    toast.info('Use a aba "Prêmios" para configurar quantidades dos 6 prêmios fixos.');
  };

  const deletePrize = async () => {
    toast.info('Prêmios são fixos no novo sistema. Apenas as quantidades podem ser editadas na aba "Prêmios".');
  };

  const creditPrize = async () => {
    toast.info('Baú agora dá pontos, não moedas. Não há mais créditos manuais para baús.');
  };

  const handleHistorySearch = () => {
    loadHistory(historyFilter.trim() || null);
  };

  const toggleDayExpanded = (date) => {
    setExpandedDays(prev => ({...prev, [date]: !prev[date]}));
  };

  // Funções do editor de regras
  const saveRules = async () => {
    setSavingRules(true);
    try {
      await mysteryBoxAPI.updateConfig({ rules: rulesContent });
      toast.success('Regras salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar regras');
    } finally {
      setSavingRules(false);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setRulesContent(editorRef.current.innerHTML);
    }
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      setRulesContent(editorRef.current.innerHTML);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
          <h2 className="text-lg sm:text-xl font-bold">Baú Misterioso</h2>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 bg-gray-50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
          <span className="text-xs sm:text-sm text-gray-600">Evento</span>
          <Switch 
            checked={config?.active || false}
            onCheckedChange={(v) => updateConfig('active', v)}
          />
          <Badge variant={config?.active ? 'default' : 'secondary'} className="text-xs">
            {config?.active ? 'ATIVO' : 'INATIVO'}
          </Badge>
          <span className="hidden sm:inline text-gray-300">|</span>
          <span className="text-xs sm:text-sm text-gray-600" title="Ligado: aparece no site (encerrado = modo consulta). Desligado: some e o link mostra EVENTO INATIVO.">
            Mostrar para o público
          </span>
          <Switch
            checked={config?.public_visible ?? !!config?.active}
            onCheckedChange={(v) => updateConfig('public_visible', v)}
            data-testid="mystery-box-visibility-switch"
          />
          <Badge variant={(config?.public_visible ?? !!config?.active) ? 'default' : 'secondary'} className="text-xs" data-testid="mystery-box-visibility-label">
            {(config?.public_visible ?? !!config?.active) ? 'VISÍVEL' : 'OCULTO'}
          </Badge>
        </div>
      </div>

      {/* Stats - 2x2 grid on mobile, 4 cols on desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <Card className="bg-purple-50">
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats?.total_boxes || 0}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">Baús Criados</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-green-600">{stats?.opened_boxes || 0}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">Abertos</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="p-2 sm:p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-blue-600">{formatNumber(stats?.total_points_distributed)}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">Pontos Distribuídos</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs - Horizontal scroll on mobile */}
      <div className="flex gap-1 sm:gap-2 border-b overflow-x-auto pb-px -mx-2 px-2 sm:mx-0 sm:px-0 scrollbar-hide">
        <button
          onClick={() => setActiveTab('jackpot')}
          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
            activeTab === 'jackpot' 
              ? 'border-red-500 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          🎰 Jackpot
        </button>
        <button
          onClick={() => setActiveTab('config')}
          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
            activeTab === 'config' 
              ? 'border-purple-500 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Config
        </button>
        <button
          onClick={() => setActiveTab('prizes')}
          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
            activeTab === 'prizes' 
              ? 'border-purple-500 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Prêmios
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap flex-shrink-0 ${
            activeTab === 'history' 
              ? 'border-blue-500 text-blue-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
          Histórico
        </button>
        <button
          onClick={() => setActiveTab('winners')}
          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap flex-shrink-0 ${
            activeTab === 'winners' 
              ? 'border-yellow-500 text-yellow-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="text-sm">🏆</span>
          Ganhadores
        </button>
        <button
          onClick={() => setActiveTab('rules')}
          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap flex-shrink-0 ${
            activeTab === 'rules' 
              ? 'border-green-500 text-green-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
          Regras
        </button>
      </div>

      {/* Tab Content */}
      
      {/* Aba Jackpot */}
      {activeTab === 'jackpot' && (
        <Card className="border-red-200">
          <CardHeader className="p-3 sm:p-6 bg-gradient-to-r from-red-50 to-orange-50">
            <CardTitle className="text-sm sm:text-base flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <span className="text-xl">🎰</span>
                Configuração do Jackpot
              </span>
              <div className="flex items-center gap-2">
                {jackpotChanged && (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300 text-[10px] sm:text-xs">
                    Alterações não salvas
                  </Badge>
                )}
                <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg">
                  <span className="text-xs text-gray-600">Ativo</span>
                  <Switch 
                    checked={localJackpot?.active || false}
                    onCheckedChange={(v) => updateLocalJackpot('active', v)}
                  />
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-3 sm:p-6">
            {/* Status Atual */}
            <div className="p-3 bg-gray-50 rounded-lg border">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="text-sm font-medium text-gray-700">Status Atual</span>
                <Badge 
                  className={
                    jackpot?.status === 'waiting' ? 'bg-yellow-100 text-yellow-700' :
                    jackpot?.status === 'available' ? 'bg-green-100 text-green-700 animate-pulse' :
                    jackpot?.status === 'cooldown' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }
                >
                  {jackpot?.status === 'waiting' ? '⏳ Aguardando' :
                   jackpot?.status === 'available' ? '🎁 LIBERADO!' :
                   jackpot?.status === 'cooldown' ? '⏰ Cooldown' :
                   '🏁 Finalizado'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
                <div className="p-2 bg-white rounded">
                  <p className="text-gray-500">Progresso</p>
                  <p className="text-lg font-bold text-orange-600">{jackpot?.progress_percent?.toFixed(1) || 0}%</p>
                </div>
                <div className="p-2 bg-white rounded">
                  <p className="text-gray-500">Total Vendido</p>
                  <p className="text-sm font-bold text-blue-600">{formatNumber(jackpot?.total_coins_sold)}</p>
                </div>
                <div className="p-2 bg-white rounded">
                  <p className="text-gray-500">Baús Abertos</p>
                  <p className="text-lg font-bold text-green-600">{jackpot?.chests_opened || 0}/{jackpot?.total_chests || 0}</p>
                </div>
                <div className="p-2 bg-white rounded">
                  <p className="text-gray-500">Restantes</p>
                  <p className="text-lg font-bold text-purple-600">{(jackpot?.total_chests || 0) - (jackpot?.chests_opened || 0)}</p>
                </div>
              </div>
            </div>
            
            {/* Configurações - Mostrar apenas Cooldown quando há sequências */}
            {/* Configuração - Apenas Cooldown (sequências definem o resto) */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              {jackpot?.sequences?.length > 0 ? (
                <p className="text-sm text-blue-700 mb-2">
                  📋 <strong>Sistema de Sequências</strong> - Meta, prêmio e ganhadores são definidos em cada sequência.
                </p>
              ) : (
                <p className="text-sm text-orange-700 mb-2">
                  ⚠️ <strong>Configure pelo menos 1 sequência</strong> para iniciar o evento.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs sm:text-sm">Cooldown (segundos)</Label>
                  <Input 
                    type="number"
                    className="h-9 sm:h-10 text-sm bg-white"
                    min="10"
                    value={localJackpot?.cooldown_seconds || ''}
                    onChange={e => updateLocalJackpot('cooldown_seconds', parseInt(e.target.value) || 300)}
                    placeholder="Ex: 300 (5 min)"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">Tempo entre baús (global)</p>
                </div>
                {jackpot?.sequences?.length > 0 && (
                  <div className="flex items-end">
                    <div className="p-2 bg-white rounded-lg border flex-1">
                      <p className="text-xs text-gray-500">Sequência Atual</p>
                      <p className="font-semibold text-purple-600">
                        {jackpot?.current_sequence?.name || `Sequência ${(jackpot?.current_sequence_index || 0) + 1}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Botões Salvar/Descartar */}
            <div className="pt-3 border-t flex flex-col-reverse sm:flex-row justify-end gap-2">
              {jackpotChanged && (
                <Button 
                  variant="outline"
                  onClick={discardJackpotChanges}
                  className="text-gray-600 h-9 sm:h-10 text-sm"
                >
                  Descartar
                </Button>
              )}
              <Button 
                onClick={saveJackpotConfig}
                disabled={!jackpotChanged || savingJackpot}
                className="bg-red-600 hover:bg-red-700 h-9 sm:h-10 text-sm"
              >
                {savingJackpot ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salvar Jackpot
              </Button>
            </div>
            
            {/* Histórico de Ganhadores */}
            {jackpot?.winners?.length > 0 && (
              <div className="pt-4 border-t">
                <Label className="text-xs sm:text-sm font-medium mb-2 block">🏆 Ganhadores</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {/* Agrupar ganhadores por baú */}
                  {(() => {
                    // Agrupar por chest_number
                    const grouped = {};
                    jackpot.winners.forEach(winner => {
                      const chestNum = winner.chest_number;
                      if (!grouped[chestNum]) {
                        grouped[chestNum] = [];
                      }
                      grouped[chestNum].push(winner);
                    });
                    
                    // Ordenar por chest_number decrescente
                    const sortedChests = Object.keys(grouped).sort((a, b) => Number(b) - Number(a));
                    
                    return sortedChests.map(chestNum => (
                      <div key={chestNum} className="bg-yellow-50 rounded-lg border border-yellow-200 p-2">
                        <div className="flex items-center gap-2 mb-2 pb-1 border-b border-yellow-300/50">
                          <span className="text-yellow-600 font-bold text-sm">Baú #{chestNum}</span>
                          <span className="text-xs text-gray-500">
                            ({grouped[chestNum].length} ganhador{grouped[chestNum].length > 1 ? 'es' : ''})
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          {grouped[chestNum].map((winner, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-1.5 bg-white/80 rounded">
                              <div className="w-7 h-7 rounded-full overflow-hidden border border-yellow-400 flex-shrink-0">
                                {winner.kako_avatar ? (
                                  <img src={winner.kako_avatar} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-yellow-200 flex items-center justify-center text-xs">👤</div>
                                )}
                              </div>
                              <span className="text-gray-700 text-sm truncate flex-1">{winner.kako_nickname}</span>
                              <span className="text-yellow-600 text-sm font-bold">{formatNumber(winner.prize_coins)} 🪙</span>
                              <Badge className={winner.coins_credited ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} variant="outline">
                                {winner.coins_credited ? '✓' : '⏳'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
            
            {/* Sistema de Sequências */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <Layers className="w-4 h-4 text-orange-500" />
                <Label className="text-sm font-medium">Sistema de Sequências</Label>
                <Badge variant="outline" className="text-xs">
                  {jackpot?.sequences?.length || 0} sequências
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Configure múltiplas sequências de baús. O sistema executa em ordem automaticamente.
              </p>
              <JackpotSequencesManager 
                jackpot={jackpot} 
                onUpdate={loadData}
              />
            </div>
            
            {/* Botão Reset */}
            <div className="pt-4 border-t space-y-2">
              <Button 
                variant="outline" 
                className="w-full text-red-600 border-red-300 hover:bg-red-50 h-9 sm:h-10 text-xs sm:text-sm"
                onClick={resetJackpot}
                data-testid="reset-jackpot-btn"
              >
                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
                Resetar Jackpot (Novo Evento)
              </Button>
              <Button 
                variant="outline" 
                className="w-full text-red-700 border-red-400 bg-red-50 hover:bg-red-100 h-9 sm:h-10 text-xs sm:text-sm"
                onClick={clearJackpotHistory}
                data-testid="clear-jackpot-history-btn"
              >
                🗑️ Apagar TODO o histórico do Jackpot (produção)
              </Button>
              <p className="text-[10px] sm:text-xs text-gray-500 text-center">
                ⚠️ Botão temporário — será removido em próxima atualização.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'config' && (
        <Card>
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-sm sm:text-base flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span>Configuração do Evento</span>
              {configChanged && (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300 text-[10px] sm:text-xs w-fit">
                  Alterações não salvas
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-3 sm:p-6 pt-0 sm:pt-0">
            <div>
              <Label className="text-xs sm:text-sm font-medium mb-2 block">Metas Diárias (moedas)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] sm:text-xs text-amber-600">🎁 Baú do Destino</Label>
                  <Input 
                    type="number"
                    className="h-9 sm:h-10 text-sm"
                    value={localConfig?.goal_1 || ''}
                    onChange={e => updateLocalConfig('goal_1', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label className="text-[10px] sm:text-xs text-purple-400">✨ Baú Místico</Label>
                  <Input 
                    type="number"
                    className="h-9 sm:h-10 text-sm"
                    value={localConfig?.goal_2 || ''}
                    onChange={e => updateLocalConfig('goal_2', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label className="text-[10px] sm:text-xs text-yellow-500">🔮 Baú do Segredo</Label>
                  <Input 
                    type="number"
                    className="h-9 sm:h-10 text-sm"
                    value={localConfig?.goal_3 || ''}
                    onChange={e => updateLocalConfig('goal_3', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>

            {/* Aviso sobre migração para pontos */}
            <div className="pt-3 sm:pt-4 border-t">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs sm:text-sm text-blue-800 font-medium">
                  ℹ️ Sistema migrado para PONTOS
                </p>
                <p className="text-[11px] sm:text-xs text-blue-700 mt-1">
                  Os baús agora dão pontos (não moedas). Configure a quantidade de cada prêmio na aba <strong>"Prêmios"</strong>.
                  Os pontos contam para o Ranking Semanal Mega Sortudos, que paga em moedas (configure os prêmios semanais abaixo).
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">Chance de Ganhar (%)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="100"
                  className="h-9 sm:h-10 text-sm"
                  value={localConfig?.win_chance || ''}
                  onChange={e => updateLocalConfig('win_chance', parseInt(e.target.value) || 0)}
                />
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">Chance do baú ter algum prêmio (vs vazio)</p>
              </div>
            </div>

            {/* Prêmios do Ranking Semanal */}
            <div className="pt-3 sm:pt-4 border-t">
              <Label className="text-xs sm:text-sm font-medium mb-2 sm:mb-3 block">
                🏆 Prêmios dos Mega Sortudos
              </Label>
              <p className="text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3">
                TOP 3 da semana (Seg a Dom)
              </p>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div>
                  <Label className="text-[10px] sm:text-xs text-yellow-500 flex items-center gap-1">
                    🥇 1º
                  </Label>
                  <Input 
                    type="number"
                    className="h-9 sm:h-10 text-sm"
                    value={localConfig?.weekly_prize_1st || ''}
                    onChange={e => updateLocalConfig('weekly_prize_1st', parseInt(e.target.value) || 0)}
                    placeholder="Moedas"
                  />
                </div>
                <div>
                  <Label className="text-[10px] sm:text-xs text-gray-400 flex items-center gap-1">
                    🥈 2º
                  </Label>
                  <Input 
                    type="number"
                    className="h-9 sm:h-10 text-sm"
                    value={localConfig?.weekly_prize_2nd || ''}
                    onChange={e => updateLocalConfig('weekly_prize_2nd', parseInt(e.target.value) || 0)}
                    placeholder="Moedas"
                  />
                </div>
                <div>
                  <Label className="text-[10px] sm:text-xs text-amber-600 flex items-center gap-1">
                    🥉 3º
                  </Label>
                  <Input 
                    type="number"
                    className="h-9 sm:h-10 text-sm"
                    value={localConfig?.weekly_prize_3rd || ''}
                    onChange={e => updateLocalConfig('weekly_prize_3rd', parseInt(e.target.value) || 0)}
                    placeholder="Moedas"
                  />
                </div>
              </div>
            </div>

            {/* Botões Salvar/Descartar - Stack on mobile */}
            <div className="pt-3 sm:pt-4 border-t flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              {configChanged && (
                <Button 
                  variant="outline"
                  onClick={discardChanges}
                  className="text-gray-600 h-9 sm:h-10 text-sm"
                >
                  Descartar
                </Button>
              )}
              <Button 
                onClick={saveAllConfig}
                disabled={!configChanged || savingConfig}
                className="bg-purple-600 hover:bg-purple-700 h-9 sm:h-10 text-sm"
              >
                {savingConfig ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salvar Configurações
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'prizes' && (
        <MysteryBoxPrizesConfig />
      )}

      {activeTab === 'history' && (
        <Card>
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-sm sm:text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
              Histórico de Prêmios
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6 pt-0 sm:pt-0">
            {/* Botão temporário: apagar todo histórico do Baú Misterioso */}
            <div className="p-3 bg-red-50 border border-red-300 rounded-lg flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
              <div>
                <p className="text-xs sm:text-sm font-semibold text-red-700">🗑️ Limpeza de Produção</p>
                <p className="text-[10px] sm:text-xs text-red-600">
                  Apaga TODO o histórico do Baú Misterioso (aberturas + Mega Sortudos). Mantém configuração de prêmios.
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                  ⚠️ Botão temporário — será removido em próxima atualização.
                </p>
              </div>
              <Button
                variant="outline"
                className="text-red-700 border-red-400 bg-white hover:bg-red-100 h-9 sm:h-10 text-xs sm:text-sm shrink-0"
                onClick={clearMysteryBoxHistory}
                data-testid="clear-mystery-box-history-btn"
              >
                Apagar Histórico
              </Button>
            </div>
            
            {/* Filtro - Stack on mobile */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1">
                <Input 
                  className="h-9 sm:h-10 text-sm"
                  placeholder="Filtrar por KAKO ID..."
                  value={historyFilter}
                  onChange={e => setHistoryFilter(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleHistorySearch()}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleHistorySearch} variant="outline" className="flex-1 sm:flex-none h-9 sm:h-10 text-xs sm:text-sm">
                  <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Buscar
                </Button>
                {historyFilter && (
                  <Button 
                    onClick={() => { setHistoryFilter(''); loadHistory(null); }} 
                    variant="ghost"
                    className="text-gray-500 h-9 sm:h-10 text-xs sm:text-sm px-2 sm:px-4"
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            {/* Resumo */}
            {history && (
              <div className="p-2 sm:p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 truncate max-w-[150px] sm:max-w-none">
                    {history.filter_kako_id ? `ID: ${history.filter_kako_id}` : 'Todos'}
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-500">Últimos {history.days} dias</p>
                </div>
                <div className="text-right">
                  <p className="text-lg sm:text-xl font-bold text-blue-600">{formatNumber(history.total_distributed)}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">distribuído</p>
                </div>
              </div>
            )}

            {/* Lista por dia - Mobile optimized */}
            <div className="space-y-2 max-h-[350px] sm:max-h-[500px] overflow-y-auto">
              {!history ? (
                <div className="text-center py-6 sm:py-8">
                  <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8 animate-spin mx-auto text-gray-400" />
                </div>
              ) : history.history.length === 0 ? (
                <p className="text-center text-gray-500 py-6 sm:py-8 text-sm">Nenhum prêmio encontrado</p>
              ) : (
                history.history.map((day) => (
                  <div key={day.date} className="border rounded-lg overflow-hidden">
                    {/* Header do dia - Simplified for mobile */}
                    <button
                      onClick={() => toggleDayExpanded(day.date)}
                      className="w-full flex items-center justify-between p-2 sm:p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                        <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                        <span className="font-semibold text-xs sm:text-sm">{formatDate(day.date)}</span>
                        <Badge variant="outline" className="text-[10px] sm:text-xs px-1 sm:px-2">
                          {day.total_boxes}
                        </Badge>
                        {day.pending_count > 0 && (
                          <Badge variant="destructive" className="text-[10px] sm:text-xs px-1 sm:px-2">
                            {day.pending_count} pend.
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="font-bold text-green-600 text-xs sm:text-sm">
                          {formatNumber(day.total_coins)} 🪙
                        </span>
                        {expandedDays[day.date] ? (
                          <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                        )}
                      </div>
                    </button>
                    
                    {/* Itens expandidos - Mobile optimized */}
                    {expandedDays[day.date] && (
                      <div className="border-t divide-y">
                        {day.items.map((item, idx) => (
                          <div 
                            key={idx}
                            className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 sm:p-3 ${
                              item.won && item.prize_coins > 0 && !item.coins_credited ? 'bg-yellow-50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                              <span className="text-base sm:text-lg">
                                {item.goal_reached === 1 ? '🎁' : item.goal_reached === 2 ? '✨' : '🔮'}
                              </span>
                              <div className="min-w-0">
                                <p className="text-xs sm:text-sm font-medium truncate">
                                  {item.kako_nickname || item.kako_id}
                                </p>
                                <p className="text-[10px] sm:text-xs text-gray-400 truncate">
                                  {item.kako_id}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                              {item.won && (item.points_won > 0 || item.prize_coins > 0) ? (
                                <span className="font-semibold text-xs sm:text-sm text-purple-600">
                                  {item.prize_name ? `${item.prize_name} ` : ''}
                                  +{formatNumber(item.points_won || item.prize_coins)} ⭐
                                </span>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-500 text-[10px] sm:text-xs">
                                  Vazio
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aba Ganhadores Semanais (Mega Sortudos) */}
      {activeTab === 'winners' && (
        <Card className="border-yellow-200">
          <CardHeader className="p-3 sm:p-6 bg-gradient-to-r from-yellow-50 to-orange-50">
            <CardTitle className="text-sm sm:text-base flex items-center gap-2">
              <span className="text-xl">🏆</span>
              Ganhadores do Ranking Semanal (Mega Sortudos)
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Lista de ganhadores agrupados por semana. Mostra quem já resgatou o prêmio.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            {/* Botão para gerar prêmios manualmente */}
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">Gerar Prêmios Manualmente</p>
                <p className="text-xs text-yellow-600">Use se os prêmios da semana anterior não foram gerados automaticamente</p>
              </div>
              <Button
                size="sm"
                onClick={handleGeneratePrizes}
                disabled={generatingPrizes}
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
              >
                {generatingPrizes ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Gift className="w-4 h-4 mr-1" />
                    Gerar Prêmios
                  </>
                )}
              </Button>
            </div>
            
            {/* Botão para limpar duplicatas (correção do bug histórico) */}
            <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-800">🧹 Limpar Duplicatas (correção)</p>
                <p className="text-xs text-red-700">
                  Remove ganhadores duplicados na mesma semana/posição. Cria índices únicos para impedir novas duplicatas.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="text-red-700 border-red-400 bg-white hover:bg-red-100 shrink-0"
                onClick={dedupeWeeklyPrizes}
                disabled={dedupingPrizes}
                data-testid="dedupe-weekly-prizes-btn"
              >
                {dedupingPrizes ? 'Limpando...' : 'Limpar Duplicatas'}
              </Button>
            </div>

            {loadingWeeklyWinners ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-yellow-500" />
                <span className="ml-2 text-gray-600">Carregando...</span>
              </div>
            ) : weeklyWinners.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl">📭</span>
                <p className="mt-2">Nenhum ganhador registrado ainda.</p>
                <p className="text-xs mt-1">Os ganhadores são gerados automaticamente no domingo.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {weeklyWinners.map((week, weekIndex) => (
                  <div key={weekIndex} className="border rounded-lg overflow-hidden">
                    {/* Header da Semana */}
                    <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-3 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-yellow-600" />
                          <span className="font-semibold text-sm">
                            Semana: {week.week_start} até {week.week_end}
                          </span>
                        </div>
                        <Badge variant="outline" className="bg-white text-xs">
                          {week.winners?.length || 0} ganhadores
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Lista de Ganhadores */}
                    <div className="divide-y">
                      {week.winners?.map((winner, winnerIndex) => (
                        <div 
                          key={winner.id || winnerIndex}
                          className={`p-3 flex items-center justify-between ${
                            winner.claimed ? 'bg-green-50' : winner.expired ? 'bg-red-50' : 'bg-white'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Posição */}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                              winner.rank === 1 ? 'bg-yellow-500' :
                              winner.rank === 2 ? 'bg-gray-400' :
                              winner.rank === 3 ? 'bg-orange-600' : 'bg-gray-300'
                            }`}>
                              {winner.rank}º
                            </div>
                            
                            {/* Avatar e Nome */}
                            <div className="flex items-center gap-2">
                              {winner.kako_avatar ? (
                                <img 
                                  src={winner.kako_avatar} 
                                  alt="" 
                                  className="w-8 h-8 rounded-full"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                  <span className="text-xs">👤</span>
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-sm">{winner.kako_nickname || 'Usuário'}</p>
                                <p className="text-xs text-gray-500">ID: {winner.kako_id}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            {/* Prêmio */}
                            <div className="text-right">
                              <p className="font-bold text-yellow-600">
                                {(winner.prize_coins || 0).toLocaleString()} 🪙
                              </p>
                              <p className="text-xs text-gray-500">moedas</p>
                            </div>
                            
                            {/* Status */}
                            {winner.claimed ? (
                              <Badge className="bg-green-500 text-white text-xs">
                                ✅ Resgatado
                              </Badge>
                            ) : winner.expired ? (
                              <Badge variant="destructive" className="text-xs">
                                ❌ Expirado
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300 text-xs">
                                ⏳ Pendente
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'rules' && (
        <Card>
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-sm sm:text-base flex items-center gap-2">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
              Regras do Evento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6 pt-0 sm:pt-0">
            {/* Barra de ferramentas do editor - Compact for mobile */}
            <div className="flex flex-wrap gap-0.5 sm:gap-1 p-1.5 sm:p-2 bg-gray-100 rounded-lg border">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => execCommand('bold')}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                title="Negrito"
              >
                <Bold className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => execCommand('italic')}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                title="Itálico"
              >
                <Italic className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Button>
              <div className="w-px h-7 sm:h-8 bg-gray-300 mx-0.5 sm:mx-1" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => execCommand('formatBlock', 'h2')}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                title="Título"
              >
                <Heading2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => execCommand('insertUnorderedList')}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                title="Lista"
              >
                <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => execCommand('insertOrderedList')}
                className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                title="Lista Numerada"
              >
                <ListOrdered className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Button>
            </div>

            {/* Editor de texto rico - Smaller height on mobile */}
            <div
              ref={editorRef}
              contentEditable
              onInput={handleEditorInput}
              dangerouslySetInnerHTML={{ __html: rulesContent }}
              className="min-h-[200px] sm:min-h-[300px] p-3 sm:p-4 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500 prose prose-sm max-w-none text-sm"
              style={{ 
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}
              data-placeholder="Digite as regras do evento aqui..."
            />

            {/* Preview e Salvar - Stack on mobile */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 pt-2 border-t">
              <p className="text-[10px] sm:text-sm text-gray-500 order-2 sm:order-1">
                Exibidas na aba "Regras" para clientes.
              </p>
              <Button 
                onClick={saveRules}
                disabled={savingRules}
                className="bg-green-500 hover:bg-green-600 w-full sm:w-auto h-9 sm:h-10 text-sm order-1 sm:order-2"
              >
                {savingRules ? (
                  <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
                )}
                Salvar Regras
              </Button>
            </div>

            {/* Preview - Collapsible on mobile */}
            {rulesContent && (
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg border">
                <p className="text-[10px] sm:text-xs text-gray-500 mb-2 font-medium">Pré-visualização:</p>
                <div 
                  className="prose prose-sm max-w-none text-sm"
                  dangerouslySetInnerHTML={{ __html: rulesContent }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MysteryBoxManagement;
